import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SurveyAnalytics {
  survey: {
    id: string;
    title: string;
    milestone: number | null;
    module_id: string | null;
    module_title?: string;
  };
  totalResponses: number;
  questions: {
    id: string;
    question_text: string;
    question_type: 'text' | 'rating' | 'multiple_choice';
    options: string[] | null;
    answers: {
      answer_text: string | null;
      answer_rating: number | null;
      answer_choice: string | null;
      user_id: string;
      completed_at: string;
    }[];
    averageRating?: number;
    choiceDistribution?: Record<string, number>;
  }[];
}

export function useSurveyAnalytics(courseId: string) {
  return useQuery({
    queryKey: ['survey-analytics', courseId],
    queryFn: async () => {
      // Fetch all surveys for the course with questions
      const { data: surveys, error: surveysError } = await supabase
        .from('course_surveys')
        .select(`
          id,
          title,
          milestone,
          module_id,
          module:modules(title),
          questions:survey_questions(
            id,
            question_text,
            question_type,
            options,
            sequence_order
          )
        `)
        .eq('course_id', courseId)
        .order('created_at', { ascending: false });

      if (surveysError) throw surveysError;

      // Fetch all responses for these surveys
      const surveyIds = surveys?.map(s => s.id) || [];
      if (surveyIds.length === 0) return [];

      const { data: responses, error: responsesError } = await supabase
        .from('survey_responses')
        .select(`
          id,
          survey_id,
          user_id,
          completed_at,
          answers:survey_answers(
            question_id,
            answer_text,
            answer_rating,
            answer_choice
          )
        `)
        .in('survey_id', surveyIds);

      if (responsesError) throw responsesError;

      // Build analytics for each survey
      const analytics: SurveyAnalytics[] = surveys?.map(survey => {
        const surveyResponses = responses?.filter(r => r.survey_id === survey.id) || [];
        
        const questionAnalytics = survey.questions
          ?.sort((a: any, b: any) => a.sequence_order - b.sequence_order)
          .map((question: any) => {
            const questionAnswers: any[] = [];
            
            surveyResponses.forEach(response => {
              const answer = response.answers?.find((a: any) => a.question_id === question.id);
              if (answer) {
                questionAnswers.push({
                  ...answer,
                  user_id: response.user_id,
                  completed_at: response.completed_at,
                });
              }
            });

            let averageRating: number | undefined;
            let choiceDistribution: Record<string, number> | undefined;

            if (question.question_type === 'rating') {
              const ratings = questionAnswers
                .map(a => a.answer_rating)
                .filter((r): r is number => r !== null);
              if (ratings.length > 0) {
                averageRating = ratings.reduce((a, b) => a + b, 0) / ratings.length;
              }
            }

            if (question.question_type === 'multiple_choice') {
              choiceDistribution = {};
              const options = question.options as string[] || [];
              options.forEach(opt => {
                choiceDistribution![opt] = 0;
              });
              questionAnswers.forEach(a => {
                if (a.answer_choice && choiceDistribution![a.answer_choice] !== undefined) {
                  choiceDistribution![a.answer_choice]++;
                }
              });
            }

            return {
              id: question.id,
              question_text: question.question_text,
              question_type: question.question_type,
              options: question.options,
              answers: questionAnswers,
              averageRating,
              choiceDistribution,
            };
          }) || [];

        return {
          survey: {
            id: survey.id,
            title: survey.title,
            milestone: survey.milestone,
            module_id: survey.module_id,
            module_title: (survey.module as any)?.title,
          },
          totalResponses: surveyResponses.length,
          questions: questionAnalytics,
        };
      }) || [];

      return analytics;
    },
    enabled: !!courseId,
  });
}
